// Media library API surface + CDN helpers. Maps to
// apps/api/src/routes/admin/media.ts. Uploads are multipart (through the Worker).

import { API_BASE, ApiError, api } from './api';

export type MediaKind = 'image' | 'audio' | 'video' | 'pdf';

export interface MediaAsset {
  id: string;
  kind: MediaKind;
  r2Key: string;
  mime: string;
  sizeBytes: number | null;
  durationMs: number | null;
  widthPx: number | null;
  heightPx: number | null;
  altKey: string | null;
  license: string | null;
  createdAt: string;
}

// Which file extensions the <input type=file> should hint per kind.
export const MEDIA_ACCEPT: Record<MediaKind, string> = {
  image: 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml',
  audio: 'audio/mpeg,audio/mp4,audio/ogg,audio/wav,audio/webm',
  video: 'video/mp4,video/webm',
  pdf: 'application/pdf',
};

// CDN base for the public bucket — mirrors the app's per-host resolution.
export function cdnBase(): string {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'admin.fajrarabi.com') return 'https://cdn.fajrarabi.com';
    if (host === 'staging-admin.fajrarabi.com') return 'https://staging-cdn.fajrarabi.com';
  }
  return (
    (import.meta.env.VITE_CDN_BASE as string | undefined) ?? 'https://staging-cdn.fajrarabi.com'
  );
}

export function mediaUrl(r2Key: string): string {
  return `${cdnBase().replace(/\/+$/, '')}/${r2Key.replace(/^\/+/, '')}`;
}

export async function listMedia(
  params: { kind?: MediaKind; q?: string },
  signal?: AbortSignal,
): Promise<MediaAsset[]> {
  const sp = new URLSearchParams();
  if (params.kind) sp.set('kind', params.kind);
  if (params.q) sp.set('q', params.q);
  const qs = sp.toString();
  const { media } = await api<{ media: MediaAsset[] }>(`/admin/media${qs ? `?${qs}` : ''}`, {
    signal,
  });
  return media;
}

// Upload goes through fetch directly (multipart body — the typed `api` helper is
// JSON-only). Mirrors its error handling.
export async function uploadMedia(input: {
  file: File;
  kind: MediaKind;
  altKey?: string;
  license?: string;
}): Promise<MediaAsset> {
  const form = new FormData();
  form.set('file', input.file);
  form.set('kind', input.kind);
  if (input.altKey) form.set('altKey', input.altKey);
  if (input.license) form.set('license', input.license);

  const resp = await fetch(`${API_BASE}/admin/media`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  const text = await resp.text();
  const parsed: unknown = text ? (JSON.parse(text) as unknown) : null;
  if (!resp.ok) {
    const e = (parsed as { error?: { code: string; message: string; details?: unknown } })?.error;
    throw new ApiError(
      resp.status,
      e?.code ?? 'unknown',
      e?.message ?? `Upload failed (${resp.status})`,
      e?.details,
    );
  }
  return (parsed as { media: MediaAsset }).media;
}

export async function updateMedia(
  id: string,
  input: { altKey?: string | null; license?: string | null },
): Promise<void> {
  await api(`/admin/media/${id}`, { method: 'PATCH', body: input });
}

export async function deleteMedia(id: string): Promise<void> {
  await api(`/admin/media/${id}`, { method: 'DELETE' });
}
